import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useEffect, useCallback, useContext } from 'react'
import Pagination from '../layout/Pagination'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faMagnifyingGlass, faEllipsisH } from '@fortawesome/free-solid-svg-icons'
import ReactFilter from '../components/inputs/ReactFilterList'
import { Table } from 'react-bootstrap'
import usePagination from '../layout/usePagination'
import { canManageRoutePlans } from '../global_context/capabilities/routePlanCapabilities'
import ActionButtonProvider from '../components/action_button/ActionButtonContext'
import DropdownActionButton from '../components/action_button/DropdownActionButton'
import { MetroSpinner } from 'react-spinners-kit'
import SeeMoreRoutePlanButton from '../components/action_button/SeeMoreRoutePlanButton'
import { DEFAULT_PER_PAGE } from '../constants/constant'
import { LayoutContext } from '../layout/MainLayout'
import LoadingScreen from '../components/LoadingScreen'
import { mapRoutePlanError } from './helpers/routePlanErrorMessages'

const RoutePlanIndexView = () => {
  const [data, setData] = useState(null)
  const title = 'Plan de visitas'
  const [filterData, setFilterData] = useState([])

  const [filters, setFilters] = useState([])
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const { pagination, setPagination, handlePaginationClick } = usePagination({
    per_page: DEFAULT_PER_PAGE
  })
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [filterVersion, setFilterVersion] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)

  ////// Stops fetchs when page is changed
  const abortController = new AbortController()
  const abortCurrentFetchs = () => {
    abortController.abort('Fetch Aborted by User.')
  }
  window.addEventListener('beforeunload', abortCurrentFetchs)
  //////

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])

      const selectedIds = labels.map((label) => labelToIdMapping[label])
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
    buildFiltersQuery()
  }

  const showCreateRoutePlanPage = () => {
    window.location = '/crear_plan_de_visita'
  }

  const filterMappings = {
    ESTADO: 'status',
    RUTA: 'uid',
    CLIENTES: 'client_uid',
    'USUARIOS ASIGNADOS': 'user_uid'
  }

  const routePlanStatusDic = {
    pending: 'En proceso...',
    created: 'Activo',
    by_integration: 'Activo',
    error: 'Error',
    disabled: 'Deshabilitado'
  }

  const statusFilterDir = [
    { name: 'Activo', value: 'created' },
    { name: 'Deshabilitado', value: 'disabled' }
  ]

  const buildFiltersQuery = () => {
    let queryParam = ''
    allSelectedOptions.forEach((value, key) => {
      if (value.length !== 0) {
        const paramKey = filterMappings[key]
        if (paramKey) {
          let selectedValues = [...value]
          queryParam += `&${paramKey}=${selectedValues.join(',')}`
        }
      }
    })
    pagination.current_page = 1
    setFilterQuery(queryParam)
  }

  const prepareFilterData = () => {
    const localLabelToIdMapping = {}
    if (filterData.length <= 0) {
      return
    }
    const clientOptions = filterData?.clients?.map((client) => {
      const displayText = `${client.uid} - ${client.name}`
      localLabelToIdMapping[displayText] = client.uid
      return displayText
    })

    const userOptions = filterData?.users?.map((user) => {
      const displayText = `${user.uid} - ${user.name}`
      localLabelToIdMapping[displayText] = user.uid
      return displayText
    })

    const routeOptions = filterData?.route_titles?.map((route) => {
      localLabelToIdMapping[route.name] = route.uid
      return route.name
    })

    const statusOptions = statusFilterDir.map((option) => {
      localLabelToIdMapping[option.name] = option.value
      return option.name
    })

    const newFilters = [
      { key: 'ESTADO', value: statusOptions },
      { key: 'RUTA', value: routeOptions },
      { key: 'CLIENTES', value: clientOptions },
      { key: 'USUARIOS ASIGNADOS', value: userOptions }
    ]
    setFilters(newFilters)
    setLabelToIdMapping(localLabelToIdMapping)
    setFetchDataStatus({ filters: true })
  }

  const resetReactFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setSearchInput('')
    setFilterVersion((prevVersion) => prevVersion + 1)
    setTriggerSearch((prev) => !prev)
  }

  useEffect(() => {
    prepareFilterData()
  }, [filterData])

  useEffect(() => {
    async function fetchAll() {
      await fetchFilterInfoFromServer()
    }
    fetchAll()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const fetchAll = async () => {
        await fetchDataFromServer()
      }
      fetchAll()
    }, 15000)
    return () => clearInterval(interval)
  }, [pagination.current_page, filterQuery, triggerSearch, searchInput])

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
    }

    fetchAll()
  }, [pagination.current_page, filterQuery, triggerSearch])

  useEffect(() => {
    if (allSelectedOptions.size > 0) {
      buildFiltersQuery()
    }
  }, [allSelectedOptions])

  const isAnyFilterSelected = () => {
    const filtersSelected = Array.from(allSelectedOptions.entries()).some(
      ([key, value]) => value.length > 0
    )
    return filtersSelected
  }

  const handleDisableRoutePlan = (model) => {
    const response = window.confirm('¿Está seguro de que desea deshabilitar este plan de visitas?')
    if (response === true) {
      authFetch(`${BASE_BACKEND_URL}/route_plans/${model.uid}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      })
        .then(() => (window.location = `/plan_de_visitas/${model.uid}`))
        .catch((e) => console.log(e))
    }
  }

  const handleCopyRoutePlan = (model) => {
    const response = window.confirm(
      '¿Está seguro de crear una copia a partir de este plan de visitas?'
    )
    if (response === true) {
      window.location = `/crear_plan_de_visita?copy=${model.uid}`
    }
  }

  const handleEditRoutePlan = (model) => {
    window.location = `/crear_plan_de_visita?edit=${model.uid}`
  }

  const fetchFilterInfoFromServer = async (loadingState = true) => {
    if (loadingState) {
      setFetchDataStatus({ ...fetchDataStatus, filters: true })
    }
    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/route_plans?filters_info=true`,
        {
          method: 'GET',
          signal: abortController.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (response) {
        setFilterData(response)
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    } finally {
      setFetchDataStatus({ ...fetchDataStatus, filters: false })
    }
  }

  const fetchDataFromServer = useCallback(async () => {
    const per_page = pagination.per_page
    const page = pagination.current_page
    try {
      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/route_plans?page=${page}&per_page=${per_page}${filterQuery || searchInput ? '&filter_search=true' : ''}${filterQuery ? filterQuery : ''}${searchInput ? `&q=${encodeURIComponent(searchInput)}` : ''}`,
        {
          method: 'GET',
          signal: abortController.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (response.status === 401) {
        return console.error(response)
      }

      const data = response
      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(data['count'] / prev.per_page)
      }))
      setData(data['result'])
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    }
  }, [pagination.current_page, filterQuery, searchInput])

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      setTriggerSearch((prev) => !prev)
      pagination.current_page = 1
    }
  }

  const handleSearch = (e) => {
    setSearchInput(e.target.value)
    pagination.current_page = 1
  }

  const handleEnterSearch = (e) => {
    if (e.key === 'Enter') {
      pagination.current_page = 1
      fetchDataFromServer()
    }
  }

  const showModel = (event, model) => {
    event.preventDefault()
    if (
      model.status == 'created' ||
      model.status == 'disabled' ||
      model.status == 'by_integration'
    ) {
      window.location = `/plan_de_visitas/${model.uid}`
    }
  }

  const showTitle = (model) => {
    switch (model.status) {
      case 'pending':
        return 'Estamos validando este plan. Los detalles estarán disponibles en breve.'
      case 'error':
        return (
          mapRoutePlanError(model.last_error) ||
          'Error al crear el plan. Revise los datos e intente de nuevo.'
        )
      case 'created':
        if (model.last_error) {
          return `La edición no se aplicó. El plan activo no cambió. ${mapRoutePlanError(model.last_error)}`
        }
        return null
      default:
        return null
    }
  }

  const renderUserNames = (model) => {
    const userNames = model.user_names || []
    if (userNames.length === 0 || model.status === 'disabled' || model.status === 'error') {
      return 'Sin usuarios asignados'
    }
    const joinedNames = userNames.join(', ')
    if (joinedNames.length > 150) {
      return (
        <SeeMoreRoutePlanButton
          id={`see-more-btn-users-${model.uid}`}
          text={joinedNames}
          uuid={model.uid}
          status={model.status}
        />
      )
    }
    return joinedNames
  }

  const filtersArray = Array.isArray(filters) ? filters : Array.from(filters || [])
  const anyFilterEmpty = filtersArray.some((filter) => filter.length === 0)

  if (data === null) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <div>
      <div>
        <div>
          <>
            <div className="index-header detailed-container">
              <h2 className="mb-20">{title}</h2>
              <div className="index-buttons">
                {canManageRoutePlans() && (
                  <button className="text-header-tittle" onClick={showCreateRoutePlanPage}>
                    Crear plan de visitas nuevo
                  </button>
                )}
              </div>
            </div>
            <div className="index-table">
              <div className="show-area">
                <h4 className="text-sub-title">Filtrar plan de vistas por:</h4>
                <div className="filters-search-container mb-10">
                  <FontAwesomeIcon className="filter-icon mr-10" icon={faFilter} />
                  <div className="filters-container">
                    {filters.map((filter, indx) => (
                      <div key={`flr-${indx}`} className="mr-10">
                        <ReactFilter
                          className="mr-10"
                          title={filter.key}
                          identifier={filter.key}
                          options={filter.value}
                          resetWatcher={filterVersion}
                          onSelectionChange={updateAllSelectedOptions}
                          disabled={anyFilterEmpty}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="search-input-containter">
                    <input
                      value={searchInput}
                      onChange={handleSearch}
                      onKeyDown={handleEnterSearch}
                      className="round-search-input"
                      style={{ fontSize: 12 }}
                      placeholder="Buscar ruta por código o nombre de ruta"
                    />
                    {searchInput === '' && (
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                    )}
                    {searchInput && (
                      <div
                        title="Limpiar texto"
                        onClick={resetReactFilters}
                        className="round-search-input-x"
                      >
                        X
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-20">
                  {isAnyFilterSelected() && (
                    <a onClick={resetReactFilters} className="reset-filter cursor-pointer mb-20">
                      Restablecer filtros aplicados
                    </a>
                  )}
                </div>
                <Table hover responsive>
                  <thead className="table-header-gray">
                    <tr>
                      <th className="vertical-align-middle" style={{ padding: '10px 25px' }}>
                        ESTADO
                      </th>
                      <th className="vertical-align-middle">CÓDIGO</th>
                      <th className="vertical-align-middle">NOMBRE DE LA RUTA</th>
                      <th className="vertical-align-middle">CLIENTES EN RUTA</th>
                      <th className="vertical-align-middle">USUARIOS ASIGNADOS</th>
                      {canManageRoutePlans() && <th className="align-center-table-column">ACCIÓN</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <ActionButtonProvider>
                      {data && data.length > 0 ? (
                        data.map((model, index) => (
                          <tr className="cursor-pointer" key={index}>
                            <td
                              className={`${model.status === 'error' ? 'td-text-limited-error td-text-color-error' : 'td-text-limited'} td-text-limited-space`}
                              id={model.uid}
                              title={showTitle(model)}
                              onClick={(event) => showModel(event, model)}
                            >
                              {model.status === 'pending' && (
                                <div className="display-inline-flex">
                                  <MetroSpinner size={16} color="#747474" />
                                  <span className="ml-10">
                                    {` ${routePlanStatusDic[model.status]}`}
                                  </span>
                                </div>
                              )}

                              {model.status === 'error' && (
                                <div className="display-inline-flex">
                                  <span class="material-symbols-outlined error-route-plan-icon">
                                    error
                                  </span>
                                  <span>{` ${routePlanStatusDic[model.status]}`}</span>
                                </div>
                              )}

                              {model.status !== 'pending' &&
                                model.status !== 'error' &&
                                routePlanStatusDic[model.status]}
                              {model.status === 'created' && model.last_error && (
                                <span
                                  className="ml-10 material-symbols-outlined error-route-plan-icon"
                                  title={showTitle(model)}
                                >
                                  warning
                                </span>
                              )}
                            </td>
                            <td
                              className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'}`}
                              id={model.uid}
                              title={showTitle(model)}
                              onClick={(event) => showModel(event, model)}
                            >
                              {model.external_uid}
                            </td>
                            <td
                              className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'} td-text-overflow`}
                              id={model.uid}
                              title={showTitle(model)}
                              onClick={(event) => showModel(event, model)}
                            >
                              {model.title}
                            </td>

                            {model.client_names.join(', ').length > 150 ? (
                              <td
                                className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'}`}
                                id={model.uid}
                                title={showTitle(model)}
                              >
                                <SeeMoreRoutePlanButton
                                  id={`see-more-btn-${model.uid}`}
                                  text={model.client_names.join(', ')}
                                  uuid={model.uid}
                                  status={model.status}
                                />
                              </td>
                            ) : (
                              <td
                                className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'}`}
                                id={model.uid}
                                title={showTitle(model)}
                                onClick={(event) => showModel(event, model)}
                              >
                                {model.client_names.join(', ')}
                              </td>
                            )}

                            <td
                              className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'}`}
                              id={model.uid}
                              title={showTitle(model)}
                              onClick={(event) => showModel(event, model)}
                            >
                              {renderUserNames(model)}
                            </td>

                            {canManageRoutePlans() && (
                              <td
                                className={`${model.status === 'error' ? 'td-text-limited-error' : 'td-text-limited'}`}
                                style={{ display: '', justifyContent: 'center' }}
                                id={model.uid}
                                title={showTitle(model)}
                                onClick={(event) => showModel(event, model)}
                              >
                                {model.status !== 'created' && model.status !== 'disabled' ? (
                                  <div className="dropdown-btn-container">
                                    <button className="dropdown-btn-disabled" type="button">
                                      <FontAwesomeIcon icon={faEllipsisH} style={{ margin: 0 }} />
                                    </button>
                                  </div>
                                ) : (
                                  <DropdownActionButton
                                    id={`btn-dropdown-${model.uid}`}
                                    list={[
                                      {
                                        disabled: model['status'] !== 'created',
                                        text: 'Editar',
                                        handler: (e) => {
                                          handleEditRoutePlan(model, e)
                                        }
                                      },
                                      {
                                        disabled:
                                          model['status'] !== 'created' &&
                                          model['status'] !== 'disabled',
                                        text: 'Crear copia',
                                        handler: (e) => {
                                          handleCopyRoutePlan(model, e)
                                        }
                                      },
                                      {
                                        disabled: model['status'] !== 'created',
                                        text: 'Deshabilitar',
                                        handler: (e) => {
                                          handleDisableRoutePlan(model, e)
                                        }
                                      }
                                    ]}
                                  />
                                )}
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="td-text-limited td-text-limited-space">
                            No hay planes de visitas creados.
                          </td>
                        </tr>
                      )}
                    </ActionButtonProvider>
                  </tbody>
                </Table>
              </div>
            </div>
            <nav className="index-table">
              {pagination.total_pages > 1 && (
                <div onClick={handlePaginationClick}>
                  <Pagination
                    current_page={pagination.current_page}
                    total_pages={pagination.total_pages}
                  />
                </div>
              )}
            </nav>
          </>
        </div>
      </div>
    </div>
  )
}
export default RoutePlanIndexView
